/**
 * Head strings and inbound links for the two dance pages, in 14 languages.
 *
 *   /ai-dance-video-generator  owns "AI dance video generator" and "AI dance
 *                              generator (from music)".
 *   /make-photo-dance          owns "make a photo dance", "photo dance AI" and
 *                              "AI dance generator from photo".
 *
 * Small on purpose: DirectorHome (the homepage chunk) reads the title and
 * description at hydration through shared/sharedComponentRouteSeo.ts, and the
 * music-video pages render one inbound sentence each. The long body copy is
 * in shared/danceSeoContent*.ts.
 *
 * Server: server/_core/seo.ts. Client: client/src/hooks/useSEO.ts,
 * client/src/pages/tools/DanceVideoGenerator.tsx,
 * client/src/components/dance/DanceInboundLink.tsx.
 */
import type { DanceSeoHead, DanceSeoLang } from "./danceSeoTypes.js";
import en from "./danceSeoHeads/en.js";
import es from "./danceSeoHeads/es.js";
import fr from "./danceSeoHeads/fr.js";
import de from "./danceSeoHeads/de.js";
import it from "./danceSeoHeads/it.js";
import pt from "./danceSeoHeads/pt.js";
import nl from "./danceSeoHeads/nl.js";
import pl from "./danceSeoHeads/pl.js";
import tr from "./danceSeoHeads/tr.js";
import ja from "./danceSeoHeads/ja.js";
import ko from "./danceSeoHeads/ko.js";
import zh from "./danceSeoHeads/zh.js";
import id from "./danceSeoHeads/id.js";
import vi from "./danceSeoHeads/vi.js";

export const DANCE_DIRECTOR_PATH = "/ai-dance-video-generator";
export const PHOTO_DANCE_PATH = "/make-photo-dance";
export const DANCE_GUIDE_SLUG = "best-ai-dance-video-generators-2026";
export const DANCE_GUIDE_PATH = `/blog/${DANCE_GUIDE_SLUG}`;

export const DANCE_SEO_HEADS: Record<DanceSeoLang, DanceSeoHead> = {
  en,
  es,
  fr,
  de,
  it,
  pt,
  nl,
  pl,
  tr,
  ja,
  ko,
  zh,
  id,
  vi,
};

export function getDanceSeoHead(lang: string): DanceSeoHead {
  return DANCE_SEO_HEADS[lang as DanceSeoLang] ?? DANCE_SEO_HEADS.en;
}

/**
 * Pages that carry one in-body link to /ai-dance-video-generator. Search
 * Console (Aug 29 - Sep 26 2026) had the homepage and
 * /ai-music-video-generator ranking for the dance page's own queries ("ai
 * dance generator from music" at #10.1 on "/", "ai dance generator" at #34 on
 * /ai-music-video-generator), and four of these pages did not link to the
 * dance page at all. One descriptive link each tells Google which page the
 * phrase belongs to.
 */
export const DANCE_INBOUND_PATHS = [
  "/",
  "/ai-music-video-generator",
  "/free-ai-music-video-generator",
  "/ai-singing-video-generator",
  "/lyric-video-generator",
  "/suno-to-video",
  "/kpop-song-generator",
  "/best-for/kpop",
  "/best-ai-music-video-generator",
] as const;

/**
 * English sentences written for the page they sit on. Pages without one, and
 * every other language, use the language's general `inbound` sentence. Each
 * sentence is true of the generator: it takes an MP3 or a WAV (a Suno song as
 * the file downloaded from Suno);
 * K-pop is one of its ten dance styles; it plans choreography for every
 * section of the song.
 */
const EN_INBOUND_BY_PATH: Record<string, string> = {
  "/ai-singing-video-generator":
    "Want the whole body moving, not only the face? The [AI dance video generator](/ai-dance-video-generator) plans choreography for every section of your song and renders a full dance video.",
  "/lyric-video-generator":
    "Want a performer dancing instead of words on screen? The [AI dance video generator](/ai-dance-video-generator) plans choreography for every section of your song.",
  "/suno-to-video":
    "Is your Suno track a dance song? Upload the same file to the [AI dance video generator](/ai-dance-video-generator) and Solmi plans choreography for every section.",
  "/kpop-song-generator":
    "Song finished? The [AI dance video generator](/ai-dance-video-generator) turns it into a K-pop dance video, with choreography planned for every section.",
  "/best-for/kpop":
    "Making a performance video? Solmi's [AI dance video generator](/ai-dance-video-generator) has a K-pop dance style and plans choreography for every section of the song.",
  "/best-ai-music-video-generator":
    "Best for dance: Solmi's [AI dance video generator](/ai-dance-video-generator) plans choreography for every section of a song and renders a full dance video.",
};

/** The inbound sentence for a page in a language, or null off the list. */
export function getDanceInboundLink(
  basePath: string,
  lang: string,
): string | null {
  if (!(DANCE_INBOUND_PATHS as readonly string[]).includes(basePath)) {
    return null;
  }
  if (lang === "en") return EN_INBOUND_BY_PATH[basePath] ?? en.inbound;
  return getDanceSeoHead(lang).inbound;
}
